import React from 'react';
import Checkbox from '@amzn/meridian/checkbox';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import checkTokens from '@amzn/meridian-tokens/base/icon/check-small';
import alertTokens from '@amzn/meridian-tokens/base/icon/alert';
import AssociateAvatar from '../../components/AssociateAvatar';

export interface AssociateNameRowProps {
  /** The associate's display name. */
  associateName?: string;
  /** The associate's login alias, used to build the badge photo URL. */
  associateAlias?: string;
  /** Whether to show the checkbox on the left. Defaults to true. */
  showCheckbox?: boolean;
  /** Checkbox checked state. Defaults to false. */
  checkboxChecked?: boolean;
  /** Whether the checkbox is disabled. Defaults to false. */
  checkboxDisabled?: boolean;
  /** Callback when the checkbox changes. Only relevant when checkbox is enabled. */
  onCheckboxChange?: (checked: boolean) => void;
  /** Whether to show the checkmark icon on the right. Defaults to false. */
  showCheckmark?: boolean;
  /** Whether to show a warning icon after the name. */
  hasWarning?: boolean;
  /** Click handler for the entire row. */
  onClick?: () => void;
  /** Whether the row is in a selected/highlighted state. */
  isSelected?: boolean;
  /** Test ID for the row container. */
  'data-testid'?: string;
}

/**
 * Shared component for displaying an associate's name row consistently
 * across the Station Assignment workflow. Used in both the WorkstationCard
 * (assigned associates) and the AssociateSidebar (unassigned associates).
 *
 * Layout: [Checkbox] [Avatar] [Name] [Warning?] [Checkmark?]
 */
const AssociateNameRow: React.FC<AssociateNameRowProps> = ({
  associateName,
  associateAlias,
  showCheckbox = true,
  checkboxChecked = false,
  checkboxDisabled = false,
  onCheckboxChange,
  showCheckmark = false,
  hasWarning = false,
  onClick,
  isSelected = false,
  'data-testid': testId,
}) => {
  const handleCheckboxChange = (nextChecked: boolean) => {
    if (onCheckboxChange) {
      onCheckboxChange(nextChecked);
    }
  };

  const rowClassName = [
    'associate-name-row',
    isSelected ? 'associate-name-row--selected' : '',
    onClick ? 'associate-name-row--clickable' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={rowClassName}
      onClick={onClick}
      data-testid={testId}
      role={onClick ? 'button' : undefined}
    >
      {showCheckbox && (
        <Checkbox
          checked={checkboxChecked}
          onChange={handleCheckboxChange}
          disabled={checkboxDisabled}
          data-testid={testId ? `${testId}-checkbox` : undefined}
        />
      )}
      <AssociateAvatar
        associateName={associateName}
        associateAlias={associateAlias}
        className="associate-name-row__avatar"
      />
      <Text type="b300" className="associate-name-row__name">
        {associateName || 'Unknown'}
      </Text>
      {hasWarning ? (
        <Icon
          tokens={alertTokens}
          className="associate-name-row__trailing-icon"
          data-testid={testId ? `${testId}-warning` : undefined}
        />
      ) : showCheckmark ? (
        <Icon
          tokens={checkTokens}
          className="associate-name-row__trailing-icon"
          data-testid={testId ? `${testId}-checkmark` : undefined}
        />
      ) : null}
    </div>
  );
};

export default AssociateNameRow;
